import {
  AllEntities,
  convertEntityTypeToObjectEntityType,
  getObjectEntityTypeUILabel,
} from "../../../../../components/entities/utils";
import Button from "../../../../../components/ui/button/Button";
import { toast } from "../../../../../components/ui/toast/Toast";
import { BidEntityType } from "../../../../types/graphQLEnums";
import { capitalize, pluralise } from "../../../../utils";
import { useAddBidEntity } from "./useAddBidEntity";
import { useRemoveBidEntities } from "./useRemoveBidEntities";

export type EntityActionParams = {
  entityType: AllEntities;
  entityId: string;
};

/**
 * React hook that returns wrapper functions for adding and removing a single entity from a bid.
 * Should all be the same entity type though
 * Both functions display toast notifications for success and error states.
 * Can be used to save/unsave to one or multiple entities to/from a bid
 *
 * @param bidId - The ID of the bid to which the entity will be added or from which it will be removed.
 * @param onRemoveEntityAction - Optional React node to be displayed as an action in the toast notification when an entity is removed.
 * @param onAddEntityAction - Optional React node to be displayed as an action in the toast notification when an entity is added.
 * @returns An object containing `addBidEntities` and `removeBidEntities` functions.
 */
export function useBidEntityActions({
  bidId,
  onRemoveEntityAction,
  onAddEntityAction,
}: {
  bidId: string;
  onRemoveEntityAction?: React.ReactNode;
  onAddEntityAction?: React.ReactNode;
}): {
  addBidEntities: (params: EntityActionParams[]) => Promise<void>;
  removeBidEntities: (params: EntityActionParams[]) => Promise<void>;
  isLoading: boolean;
} {
  // Assuming that only one entity type is being added at a time -
  const { mutateAsync: addEntities, isLoading: isAdding } = useAddBidEntity({
    onSuccess: (data, variables) => {
      const addedEntity = data.addBidEntities[0];
      if (addedEntity) {
        const messageText =
          data.addBidEntities.length > 1
            ? pluralise(
                data.addBidEntities.length,
                getObjectEntityTypeUILabel(addedEntity.entityType),
                undefined,
              )
            : capitalize(getObjectEntityTypeUILabel(addedEntity.entityType));

        toast.success(`${messageText} saved to ${addedEntity.bidName}`, {
          action: <>{onAddEntityAction}</>,
        });
      } else {
        // The mutation returns only the rows it inserted, so an empty result means everything
        // requested was already on the bid
        const requested = variables.entities.entities[0];
        const label = requested ? getObjectEntityTypeUILabel(requested.entityType) : "entity";
        toast.info(`This ${label} is already saved to this bid`);
      }
    },
    onError: (_, variables) => {
      const entity = variables.entities.entities[0];
      toast.error(`Failed to add ${entity?.entityType ?? "entity"} to this bid`);
    },
  });

  const addBidEntities = async (addedEntities: EntityActionParams[]) => {
    await addEntities({
      id: bidId,
      entities: {
        entities: addedEntities.map(({ entityId, entityType }) => ({
          entityId,
          entityType: convertEntityTypeToObjectEntityType(entityType, BidEntityType),
        })),
      },
    });
  };

  // --- REMOVE HOOK ----------------------------------------------------------
  const { mutateAsync: removeEntities, isLoading: isRemoving } = useRemoveBidEntities({
    onSuccess: (data) => {
      // Assuming only one entity type is being removed at a time
      const removedEntity = data.removeBidEntities[0];

      if (!removedEntity) {
        // Should not happen, but having it just in case
        toast.error(`Error removing entity from this bid`);
        return;
      }

      const messageText =
        data.removeBidEntities.length > 1
          ? pluralise(
              data.removeBidEntities.length,
              getObjectEntityTypeUILabel(removedEntity.entityType),
              undefined,
            )
          : capitalize(getObjectEntityTypeUILabel(removedEntity.entityType));

      toast.success(`${messageText} removed from ${removedEntity.bidName}`, {
        action: (
          <>
            {onRemoveEntityAction ?? (
              <Button
                buttonType="tertiary"
                onClick={async () =>
                  await addEntities({
                    id: bidId,
                    entities: {
                      entities: data.removeBidEntities.map(({ entityId, entityType }) => ({
                        entityId,
                        // Annoying, but they are the same type. Typescript acting a fool here
                        entityType: entityType as unknown as BidEntityType,
                      })),
                    },
                  })
                }
              >
                Undo
              </Button>
            )}
          </>
        ),
      });
    },
    onError: (_, variables) => {
      const removedEntity = variables.entities.entities[0];
      const entityType = getObjectEntityTypeUILabel(removedEntity.entityType);
      toast.error(`Failed to remove ${entityType} from this bid`);
    },
  });

  const removeBidEntities = async (removedEntities: EntityActionParams[]) => {
    await removeEntities({
      id: bidId,
      entities: {
        entities: removedEntities.map(({ entityId, entityType }) => ({
          entityId,
          entityType: convertEntityTypeToObjectEntityType(entityType, BidEntityType),
        })),
      },
    });
  };

  return {
    addBidEntities,
    removeBidEntities,
    isLoading: isAdding || isRemoving,
  };
}
